/**
 * COLOUR TEMPERATURE — product-concept §11's "full sensory setting", measured rather than claimed.
 *
 * Authorised by: product-concept §11 (which lists "colour temperature" among the DECIDED "full
 * sensory settings"), §12 ("sensory and accessibility settings are free forever, categorically"),
 * `docs/DEVIATIONS.md` M11 and RULINGS **R182**, which moved Llanw to the free side so that "no
 * user has to pay to escape a material that tires them".
 *
 * There is no Intensity channel for colour temperature anywhere in this codebase: it is delivered
 * only by the material picker, so whether the free tier offers a choice of temperature is a
 * question about `MATERIAL_TIER` and about pixels, and this file is the only place it is answered
 * with numbers.
 *
 * ─────────────────────────────────────────────────────────────────────────────────────────
 * THIS FILE IS A PROJECTION, NOT A SECOND TABLE — and that is why it is not in `materials.ts`
 * ─────────────────────────────────────────────────────────────────────────────────────────
 *
 * The colour whose temperature matters is the one the user is actually looking at: the page
 * ground, which is the largest painted area on every surface in the app. That colour is not a
 * ramp stop a material declares; it is what `resolveTheme` resolves, and it differs by SCHEME —
 * `MaterialRole`'s own doc says `ground` is "the light scheme's page ground" and `neutral` is
 * "the dark scheme's grounds" (`theme.ts`'s `groundFor`: light `background` is `ground[3]`, dark
 * `background` is `neutral[11]`). So this record reads `resolveTheme(...).colours.background`
 * itself. It cannot live in `materials.ts`, because `theme.ts` imports `materials.ts` and the
 * reverse import would be a cycle — the same reason `widget.ts` sits last in `index.ts`.
 *
 * ─────────────────────────────────────────────────────────────────────────────────────────
 * THREE METRICS HAVE STOOD HERE. THE HISTORY OF THE FIRST TWO IS THE ARGUMENT FOR THE THIRD.
 * ─────────────────────────────────────────────────────────────────────────────────────────
 *
 * 1. **`ramps.ground[4]`, no magnitude gate at all.** Sampled a light-scheme ramp stop for both
 *    schemes, so it described a colour half the users never see; and with nothing standing in for
 *    "is this a hue at all", `hueAngle`'s documented "undefined for true greys, reported as 0"
 *    made a pure grey classify as hue 0 — red — which is warm. `#808080` was maximally warm. Both
 *    holes are shut and stay shut (`test/materials.test.ts` pins the grey case explicitly).
 *
 * 2. **`RED_CHROMA_FLOOR` (0.12) on `chroma`, WHICH CANNOT MEASURE A DARK GROUND.** This one
 *    passed every test and produced a false verdict — "the dark scheme has no colour temperature
 *    at all" — which then travelled into `docs/DEVIATIONS.md` and into a founder question.
 *
 *    `chroma` is `(max − min) / 255`: an sRGB code-value spread, and therefore **luminance-blind**.
 *    A dark page ground has no channel above `0x26`, so the largest spread it can physically
 *    express is bounded at or below the floor before any pixel is chosen:
 *
 *      | dark ground        | brightest channel | max expressible chroma | vs floor 0.120 |
 *      |--------------------|-------------------|------------------------|----------------|
 *      | drystone `#14181C` | `0x1C`            | 0.110                  | UNREACHABLE    |
 *      | calch `#1F160C`    | `0x1F`            | 0.122                  | floor is 99%   |
 *      | llanw `#09191F`    | `0x1F`            | 0.122                  | floor is 99%   |
 *      | ffion `#22131D`    | `0x22`            | 0.133                  | floor is 90%   |
 *      | glo / grug         | `0x24` / `0x26`   | 0.141 / 0.149          | 85% / 81%      |
 *
 *    So "every dark ground is neutral" was a property of the threshold, not of the palette, and
 *    Drystone's dark verdict in particular was decided before a pixel was measured. The repo
 *    refuted it in the next file along: the visibility gate asserts, and passes, that every pair
 *    of DARK page grounds differs by ΔE00 ≥ 3 — Calch vs Llanw 14.43, Grug vs Calch 15.11, Llanw
 *    vs Ffion 18.87 — against a suite whose "unmistakable somewhere prominent" bar is ΔE 10. The
 *    prose said so three times too: Calch's dark is "a warm stone dark rather than a night sky —
 *    the one material whose two schemes are both warm", Glo's is "a true blue-black", and
 *    `nightChroma` exists as a CEILING precisely because ember-on-dark warmth is visible enough to
 *    need capping. `RED_CHROMA_FLOOR`'s own doc calls 0.12 the point where a dark warm tint stops
 *    being a hint and becomes a hue: it was calibrated to PERMIT low-chroma dark warmth, so
 *    reusing it to declare that same warmth nonexistent inverted the argument it was written for.
 *    One number for tidiness is not a correctness argument.
 *
 *    It was wrong in light too, and there it inverted the perceptual ordering rather than
 *    flattening it: `chroma` put Grug — "the vivid material", purple the whole way through —
 *    below the floor at 0.110, because its light ground `#E3E3FF` already has blue at `0xFF` and
 *    therefore a ceiling of 0.110. Same arithmetic, same manufactured neutral.
 *
 * 3. **What is used now: ΔE00 against the grey of the same lightness.** `colour.ts` ships
 *    `deltaE2000`, and its doc calls ΔE "the gate a materials feature cannot do without". A ground
 *    has a temperature exactly when it is not the same colour as a neutral grey of its own
 *    lightness — a perceptual question, answered with the perceptual metric this package already
 *    owns. `atLuminance` supplies the grey: relative luminance determines CIELAB L\* monotonically,
 *    so "grey at the same relative luminance" is "grey at the same L\*".
 *
 *    IT IS NOT GAMUT-BOUNDED, which is the whole point. Swept over sRGB at each ground's own
 *    luminance, the largest reachable distance-from-grey is **31.3 in light** and **27.2 in dark**
 *    (`#000069`, at the dark grounds' lightness). The floors below are 13% and 11% of that range,
 *    where `chroma`'s floor was 81–110% of its own.
 *
 * ─────────────────────────────────────────────────────────────────────────────────────────
 * THE FLOOR, AND WHY IT IS NOT A NEW NUMBER
 * ─────────────────────────────────────────────────────────────────────────────────────────
 *
 * `test/materials.test.ts`'s visibility gate already states how far apart two PAGE GROUNDS must
 * sit before a user can be said to see a difference between them: `FLOOR.background`, ΔE00 4 in
 * light and 3 in dark. The question here is the same question about the same token — is this
 * ground distinguishable from a grey? — so that pair of numbers is borrowed whole, per-scheme
 * split included, rather than a third threshold being invented. That test asserts the two records
 * are equal, so borrowing cannot quietly become diverging.
 *
 * The DIRECTION rule is unchanged, and nothing found fault with it: `WARM_HUE_TOLERANCE`, 90°
 * either side of red, which puts pink warm and violet cool exactly as the materials' own prose
 * does. Every one of the twelve grounds clears that boundary by at least 30° (tightest: Grug),
 * against the 20° margin `test/materials.test.ts` requires, so no verdict here turns on hue by a
 * hair.
 *
 * ─────────────────────────────────────────────────────────────────────────────────────────
 * MEASURED. `background`, invariant across all 125 colour × contrast × decor combinations —
 * five levels on each of three channels, with the dial pinned at 2, which cannot reach
 * `background` once all three of its channels are overridden. Both schemes. Swept rather than
 * trusted in `test/materials.test.ts`.
 * ─────────────────────────────────────────────────────────────────────────────────────────
 *
 * | material | tier | light ground        | ΔE-grey | verdict | dark ground          | ΔE-grey | verdict |
 * |----------|------|---------------------|---------|---------|----------------------|---------|---------|
 * | drystone | free | `#EAE5DA` 41° wool  |    5.26 | warm    | `#14181C` 210° slate |    3.34 | cool    |
 * | calch    | plus | `#F2E4C9` 40°       |   11.17 | warm    | `#1F160C`  32°       |    6.99 | warm    |
 * | glo      | plus | `#D9E6FD` 218°      |    9.81 | cool    | `#101824` 216°       |    7.81 | cool    |
 * | grug     | plus | `#E3E3FF` 240°      |   11.58 | cool    | `#151626` 236°       |   10.12 | cool    |
 * | llanw    | free | `#C4EDF4` 189°      |   13.40 | cool    | `#09191F` 196°       |    7.40 | cool    |
 * | ffion    | plus | `#FFDCEA` 336°      |   14.52 | warm    | `#22131D` 320°       |   11.32 | warm    |
 *
 * BOTH VERDICTS THAT MOVED WHEN THE METRIC WAS FIXED ARE DRYSTONE'S. Under `chroma` it read
 * `neutral` in both schemes. It is neither, and the reason is structural rather than marginal:
 * **Drystone's two schemes are drawn from two different ramps with opposite temperatures.** Light
 * `background` is `wool[3]`, the "weathered putty ground" (`theme.ts`); dark `background` is
 * `slate[11]`, and `theme.ts` says in as many words that "Drystone's level 0 is a blue-grey".
 * A single verdict covering both schemes could never have been right for this material.
 *
 * THE ONE THIN CALL, NAMED RATHER THAN BURIED. Drystone's dark ground clears its floor by 0.34
 * (3.34 against 3.0); every other ground clears by at least 1.26. The verdict is load-bearing —
 * at a flat floor of 4.0 it would read `neutral`, and the free set in dark would then span two
 * temperatures instead of one, which is the difference between the failing test below and a
 * passing one. Two independent things say `cool` is right anyway. Its ramp is `slate`, named a
 * blue-grey by the file that defines the scheme; and the page ground is the LOWEST-chroma sample
 * of a uniformly cool dark scheme — the surface above it sits at 4.55 and the deep ground at 3.77,
 * both at hue 210 — so the page understates a temperature the rest of the scheme carries plainly.
 * The number is in the table rather than rounded off, because the next reader has to be able to
 * check this one for themselves.
 *
 * WHY NOT DERIVE FROM THE ACCENT, which does carry chroma (0.18–0.48) in both schemes: because it
 * contradicts every material's own prose. Calch is "the bright material", warm by design, and its
 * accent is steel-blue sea holly — deliberately "a cool accent on a warm ground". Ffion is "the
 * one warm-chromatic corner of the set" and its accent is deep violet. An accent-derived table
 * would call Calch cool and Grug warm, which is not what a user sees. One flash of colour on a
 * focus ring is not the temperature of a screen; the page under it is.
 *
 * WHAT THIS TABLE SAYS THAT THE PREVIOUS ONE HID. In the DARK scheme the free set is
 * `{cool, cool}` — Drystone and Llanw both — while the only warm dark materials, Calch and Ffion,
 * are both paid. A dark-mode user tired by coolness has nowhere free to go, which is the exact
 * failure R182 moved Llanw to prevent. That is asserted as a failing test rather than described,
 * and filed for a founder ruling in `docs/OPEN-QUESTIONS.md`. In the LIGHT scheme the free set is
 * `{warm, cool}` and R182's build consequence holds as worded.
 */

import { type MaterialId, MATERIAL_IDS } from '@carn/core';

import { atLuminance, deltaE2000, hueAngle, hueDistance } from './colour';
import { relativeLuminance } from './contrast';
import { DEFAULT_INTENSITY } from './intensity';
import { resolveTheme, type ColourScheme } from './theme';

/**
 * `'neutral'` is a real answer, not a failure to decide: a ground indistinguishable from a grey of
 * its own lightness has no temperature, so there is nothing about it for a user to find tiring and
 * nothing to escape. No shipped material's page ground is neutral under the table above — the
 * verdict exists for the achromatic case, which `test/materials.test.ts` pins.
 */
export type ColourTemperature = 'warm' | 'cool' | 'neutral';

/**
 * Hue-wheel half-width counted as "warm" (red-orange-yellow, and the pink side of magenta),
 * centred on red (hue 0); the other half (blue-green-violet) is cool. Nothing in the shipped set
 * sits near this boundary — the tightest is Grug at 30° of margin — and `test/materials.test.ts`
 * asserts a 20° margin so a future material cannot land on it and make the verdict a coin flip.
 */
export const WARM_HUE_TOLERANCE = 90;

/**
 * How far a ground must sit from the grey of its own lightness, in ΔE00, before it has a
 * temperature at all.
 *
 * These are `test/materials.test.ts`'s `FLOOR.background` — the distance that gate already
 * requires between two materials' page grounds before it will call them different colours — and
 * that test asserts the two records are equal, so borrowing cannot silently become diverging.
 * Per-scheme because the borrowed number is per-scheme, not because dark needed loosening: dark
 * grounds reach 27.2 on this metric, so 3.0 is 11% of the available range.
 */
export const TEMPERATURE_DELTA_FLOOR: Record<ColourScheme, number> = { light: 4, dark: 3 };

/** Any grey seeds it: `atLuminance` blends toward white or black, so a grey stays a grey. */
const GREY_SEED = '#808080';

/**
 * The neutral grey at this colour's lightness. Relative luminance determines CIELAB L\*
 * monotonically, so matching luminance matches L\* — and that is what makes the comparison below
 * a measure of colourfulness rather than of brightness.
 */
export function greyAtSameLightness(hex: string): string {
  return atLuminance(GREY_SEED, relativeLuminance(hex));
}

/**
 * How far this colour is from being grey, in ΔE00 — the luminance-normalised replacement for
 * `chroma` in this file. Reads 0 for any grey, up to 31.3 at the most colourful sRGB colour
 * available at a light page ground's lightness and 27.2 at a dark one's.
 */
export function chromaticDistance(hex: string): number {
  return deltaE2000(hex, greyAtSameLightness(hex));
}

/**
 * The temperature of one literal colour, judged as a page ground in `scheme` — the scheme selects
 * the floor and nothing else. Achromatic in, `'neutral'` out — never `'warm'`.
 */
export function colourTemperatureOf(hex: string, scheme: ColourScheme): ColourTemperature {
  if (chromaticDistance(hex) < TEMPERATURE_DELTA_FLOOR[scheme]) return 'neutral';
  return hueDistance(hueAngle(hex), 0) <= WARM_HUE_TOLERANCE ? 'warm' : 'cool';
}

/** The literal page ground a user of this material, in this scheme, is looking at. */
export function pageGroundOf(scheme: ColourScheme, material: MaterialId): string {
  return resolveTheme({ intensity: DEFAULT_INTENSITY, scheme, material }).colours.background;
}

/**
 * Every material's page-ground temperature, PER SCHEME — because there is no such thing as a
 * material's temperature independent of the scheme it is rendered in, and pretending otherwise
 * is what the first version of this record got wrong.
 */
export const MATERIAL_COLOUR_TEMPERATURE: Record<
  ColourScheme,
  Record<MaterialId, ColourTemperature>
> = {
  light: temperaturesFor('light'),
  dark: temperaturesFor('dark'),
};

function temperaturesFor(scheme: ColourScheme): Record<MaterialId, ColourTemperature> {
  return Object.fromEntries(
    MATERIAL_IDS.map((id) => [id, colourTemperatureOf(pageGroundOf(scheme, id), scheme)]),
  ) as Record<MaterialId, ColourTemperature>;
}
